// components/ChallengeAdminTab.jsx
import React, { useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash, faMinus } from '@fortawesome/free-solid-svg-icons';
import ChallengeModal from './ChallengeModal'; // Import the ChallengeModal

function ChallengeAdminTab({
  isAdmin, challengePool, selectedChallenges,
  toggleChallengeSelection, toggleCategorySelection,
  addCategory, renameCategory, deleteCategory,
  addOrUpdateChallenge, deleteChallenge
}) {
  const [showChallengeModal, setShowChallengeModal] = useState(false);
  const [editingChallenge, setEditingChallenge] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});

  const isCategoryFullySelected = useCallback((catName) => {
    const catChallenges = challengePool.find(c => c.category === catName)?.challenges || [];
    // A category is fully selected if all its challenges are selected
    // and each is present 'weight' times in the selectedChallenges array.
    return catChallenges.every(ch =>
      selectedChallenges.filter(sel => sel.category === catName && sel.id === ch.id).length === (ch.weight || 1)
    );
  }, [challengePool, selectedChallenges]);


  const handleAddCategory = () => {
    if (newCategoryName.trim()) {
      addCategory(newCategoryName.trim());
      setNewCategoryName('');
    }
  };

  const handleRenameCategory = (oldName) => {
    const newName = prompt(`Neuer Name für Kategorie "${oldName}":`);
    if (newName && newName.trim() && newName.trim() !== oldName) {
      renameCategory(oldName, newName.trim());
    }
  };

  const handleDeleteCategory = (categoryName) => {
    if (window.confirm(`Soll die Kategorie "${categoryName}" wirklich gelöscht werden? Alle Challenges darin gehen verloren!`)) {
      deleteCategory(categoryName);
    }
  };

  const handleAddOrUpdateChallenge = (originalCategory, category, challengeData) => {
    addOrUpdateChallenge(originalCategory, category, challengeData);
    setShowChallengeModal(false);
    setEditingChallenge(null);
  };

  const handleDeleteChallenge = (category, challengeId) => {
    if (window.confirm('Soll diese Challenge wirklich gelöscht werden?')) {
      deleteChallenge(category, challengeId);
    }
  };

  const toggleCategoryExpand = useCallback((category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  }, []);

  return (
    <div className="p-4 bg-surface rounded-xl shadow-lg border border-border">
      <h2 className="text-2xl font-bold mb-6 text-text">Herausforderungs-Verwaltung</h2>

      {/* Challenge Edit/Add Modal */}
      {showChallengeModal && (
        <ChallengeModal
          challenge={editingChallenge}
          onClose={() => {
            setShowChallengeModal(false);
            setEditingChallenge(null);
          }}
          onSave={handleAddOrUpdateChallenge}
          challengePool={challengePool}
        />
      )}

      {/* Add New Category Section */}
      {isAdmin && (
        <div className="mb-8 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg shadow-sm border border-border">
          <h3 className="text-xl font-semibold mb-3 text-text">Neue Kategorie hinzufügen</h3>
          <div className="flex gap-3">
            <input
              type="text"
              placeholder="Name der neuen Kategorie"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              className="flex-grow shadow appearance-none border rounded py-2 px-3 text-text-light leading-tight focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-background"
            />
            <button
              onClick={handleAddCategory}
              disabled={!newCategoryName.trim()}
              className="bg-primary hover:bg-primary-dark text-white font-bold py-2 px-4 rounded-lg shadow-md"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" /> Hinzufügen
            </button>
          </div>
        </div>
      )}

      {/* Available Challenges Display and Selection */}
      <h3 className="text-xl font-semibold mb-4 text-text">Verfügbare Herausforderungen</h3>
      {challengePool.length === 0 ? (
        <p className="text-text-light italic">Noch keine Herausforderungen verfügbar. Füge eine neue Kategorie und Challenges hinzu.</p>
      ) : (
        <div className="space-y-4">
          {challengePool.map(cat => (
            <div key={cat.category} className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg shadow-sm border border-border">
              <div className="flex justify-between items-center mb-3">
                <h4
                  className="text-lg font-bold text-text cursor-pointer hover:text-primary transition-colors"
                  onClick={() => toggleCategoryExpand(cat.category)}
                >
                  <FontAwesomeIcon icon={expandedCategories[cat.category] ? faMinus : faPlus} className="mr-2" />
                  {cat.category} ({cat.challenges.length})
                </h4>
                {isAdmin && (
                  <div className="flex gap-2">
                    <input type="checkbox" checked={isCategoryFullySelected(cat.category)} onChange={() => toggleCategorySelection(cat.category)} />
                    <button
                      onClick={() => handleRenameCategory(cat.category)}
                      className="text-blue-500 hover:text-blue-700"
                      title="Kategorie umbenennen"
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button
                      onClick={() => handleDeleteCategory(cat.category)}
                      className="text-red-500 hover:text-red-700"
                      title="Kategorie löschen"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                )}
              </div>

              {expandedCategories[cat.category] && (
                <div className="space-y-3 mt-4">
                  {cat.challenges.length === 0 ? (
                    <p className="text-text-light italic pl-6">Keine Challenges in dieser Kategorie.</p>
                  ) : (
                    cat.challenges.map(challenge => (
                      <div
                        key={challenge.id}
                        className={`flex items-center justify-between p-3 rounded-md border ${selectedChallenges.some(sel => sel.category === cat.category && sel.id === challenge.id) ? 'bg-primary-light border-primary text-primary-dark' : 'bg-background border-border text-text-light'}`}
                      >
                        <div className="flex-grow text-sm space-y-1">
                          <div className="flex justify-between items-center">
                            <p className="font-semibold mr-2">Positiv: {challenge.positive}</p>
                            <span className="text-sm text-text-light whitespace-nowrap">
                              ⏱️ Dauer: {challenge.durationPositive ?? '∞'}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <p className="mr-2">Negativ: {challenge.negative}</p>
                            <span className="text-sm text-text-light whitespace-nowrap">
                              ⏱️ Dauer: {challenge.durationNegative ?? '∞'}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            🧮 Gewichtung: {challenge.weight ?? 1}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <input
                            type="checkbox"
                            checked={selectedChallenges.some(sel => sel.category === cat.category && sel.id === challenge.id)}
                            onChange={() => toggleChallengeSelection(cat.category, challenge.id)}
                            className="form-checkbox h-5 w-5 text-primary rounded focus:ring-primary"
                          />
                          {isAdmin && (
                            <>
                              <button
                                onClick={() => { setEditingChallenge({ ...challenge, category: cat.category }); setShowChallengeModal(true); }}
                                className="text-blue-500 hover:text-blue-700"
                                title="Challenge bearbeiten"
                              >
                                <FontAwesomeIcon icon={faEdit} />
                              </button>
                              <button
                                onClick={() => handleDeleteChallenge(cat.category, challenge.id)}
                                className="text-red-500 hover:text-red-700"
                                title="Challenge löschen"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                  {isAdmin && (
                    <button
                      onClick={() => { setEditingChallenge({ category: cat.category, positive: '', negative: '' }); setShowChallengeModal(true); }}
                      className="mt-3 w-full bg-accent hover:bg-accent-dark text-white font-bold py-2 px-4 rounded-lg shadow-md"
                    >
                      <FontAwesomeIcon icon={faPlus} className="mr-2" /> Neue Challenge in "{cat.category}"
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ChallengeAdminTab;